import React from 'react';
import Icon from '../../../components/AppIcon';

// Minimal inline SVG sparkline — no external chart lib needed
const Sparkline = ({ data, color = '#f59e0b' }) => {
  if (!data || data?.length < 2) return null;
  const width = 100;
  const height = 32;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data?.length - 1);

  const points = data?.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });

  const pathD = `M ${points?.join(' L ')}`;

  // Area fill
  const areaD = `M ${points?.[0]} L ${points?.join(' L ')} L ${(data?.length - 1) * step},${height} L 0,${height} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" className="overflow-visible">
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill="url(#sparkGrad)" />
      <path d={pathD} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Last point dot */}
      <circle
        cx={(data?.length - 1) * step}
        cy={height - ((data?.[data?.length - 1] - min) / range) * (height - 4) - 2}
        r="3"
        fill={color}
      />
    </svg>
  );
};

// Formats the real period_month (YYYY-MM-DD) the fetched quota row belongs to, e.g. "Aug 2026" —
// deliberately not today's date, since mi_release_orders can lag behind the calendar month.
const formatQuotaMonth = (periodMonth) => {
  if (!periodMonth) return null;
  const d = new Date(periodMonth);
  if (isNaN(d?.getTime())) return null;
  return d?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const MIDataPanel = ({ data }) => {
  const {
    thisMonthQuota,
    quotaPeriodMonth,
    trend12m,
    stateRank,
    nationalRank,
    yoyChange,
    yoyPositive,
  } = data;

  const monthLabel = formatQuotaMonth(quotaPeriodMonth);
  const quotaLabel = monthLabel ? `${monthLabel} Quota` : "This Month's Release Quota";

  return (
    <div
      className="rounded-xl p-4 mt-3"
      style={{
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 60%, #fde68a22 100%)',
        border: '1px solid #fde68a',
      }}
    >
      {/* Header label */}
      <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#92400e' }}>
        Market Intelligence
      </p>

      {/* Main quota + sparkline row */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-medium mb-0.5" style={{ color: '#78350f' }}>{quotaLabel}</p>
          <p className="text-2xl font-bold leading-tight" style={{ color: '#1f2937' }}>
            {thisMonthQuota?.toLocaleString('en-IN')} <span className="text-base font-semibold" style={{ color: '#92400e' }}>MT</span>
          </p>
        </div>
        <div className="flex-shrink-0">
          <Sparkline data={trend12m} color="#f59e0b" />
          <p className="text-[10px] text-center mt-1" style={{ color: '#a16207' }}>12-month trend</p>
        </div>
      </div>

      {/* Rank + YoY row */}
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <span
          className="px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
        >
          {stateRank ? `State Rank #${stateRank}` : 'State Rank — Not Ranked'}
        </span>
        <span
          className="px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}
        >
          {nationalRank ? `National Rank #${nationalRank}` : 'National Rank — Not Ranked'}
        </span>
        <span
          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
          style={{
            background: yoyPositive ? '#dcfce7' : '#fee2e2',
            color: yoyPositive ? '#16a34a' : '#DC143C',
            border: `1px solid ${yoyPositive ? '#bbf7d0' : '#fecaca'}`,
          }}
        >
          <Icon name={yoyPositive ? 'TrendingUp' : 'TrendingDown'} size={12} color={yoyPositive ? '#16a34a' : '#DC143C'} />
          {yoyPositive ? '+' : ''}{yoyChange}% YoY
        </span>
      </div>

      {/* Caption */}
      <p className="text-[10px] mt-2.5 font-medium" style={{ color: '#a16207' }}>
        Data from SugarHathi Market Intelligence
      </p>
    </div>
  );
};

export default MIDataPanel;
